import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

const OTPContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin: 1.5rem 0;
`;

const OTPBox = styled.input<{ hasError?: boolean }>`
  width: 3rem;
  height: 3.5rem;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  border: 2px solid ${props => props.hasError ? props.theme.error : props.theme.inputBorder};
  border-radius: 8px;
  background: ${props => props.theme.inputBackground};
  color: ${props => props.theme.textPrimary};
  transition: all 0.3s;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? props.theme.error : props.theme.primary};
    box-shadow: 0 0 0 3px ${props => props.hasError ? props.theme.error : props.theme.primary}20;
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (otp: string) => void;
  onComplete?: (otp: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  autoFocus?: boolean;
}

const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  hasError = false,
  autoFocus = true
}) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  // Initialize OTP array from value prop
  useEffect(() => {
    const otpArray = value.split('').slice(0, length);
    while (otpArray.length < length) {
      otpArray.push('');
    }
    setOtp(otpArray);
  }, [value, length]);

  // Auto-focus first input
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Web OTP API - Auto-capture OTP from SMS (Android Chrome)
  useEffect(() => {
    if (!('OTPCredential' in window)) {
      return;
    }

    const ac = new AbortController();

    // Request OTP credentials
    navigator.credentials
      .get({
        // @ts-ignore - OTPCredential is not in TypeScript types yet
        otp: { transport: ['sms'] },
        signal: ac.signal
      })
      .then((credential: any) => {
        if (credential?.code) {
          const code = credential.code;
          handleAutoFill(code);
        }
      })
      .catch(() => {
        // Silently handle auto-capture errors
      });

    return () => {
      ac.abort();
    };
  }, []);

  const handleAutoFill = (code: string) => {
    const otpArray = code.split('').slice(0, length);
    while (otpArray.length < length) {
      otpArray.push('');
    }
    setOtp(otpArray);
    onChange(code);

    if (onComplete && code.length === length) {
      onComplete(code);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (disabled) return;

    // Only allow numbers
    const numericValue = value.replace(/[^0-9]/g, '');

    if (numericValue.length === 0) {
      // Handle backspace/delete
      const newOtp = [...otp];
      newOtp[index] = '';
      setOtp(newOtp);
      onChange(newOtp.join(''));
      return;
    }

    // Handle paste or multiple characters
    if (numericValue.length > 1) {
      const otpArray = numericValue.split('').slice(0, length);
      const newOtp = [...otp];

      otpArray.forEach((char, idx) => {
        if (index + idx < length) {
          newOtp[index + idx] = char;
        }
      });

      setOtp(newOtp);
      const otpString = newOtp.join('');
      onChange(otpString);

      // Focus last filled input or next empty
      const nextIndex = Math.min(index + otpArray.length, length - 1);
      inputRefs.current[nextIndex]?.focus();

      // Check if complete
      if (onComplete && otpString.length === length && !otpString.includes('')) {
        onComplete(otpString);
      }
      return;
    }

    // Handle single character
    const newOtp = [...otp];
    newOtp[index] = numericValue;
    setOtp(newOtp);
    onChange(newOtp.join(''));

    // Auto-focus next input
    if (numericValue && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Check if complete
    const otpString = newOtp.join('');
    if (onComplete && otpString.length === length && !otpString.includes('')) {
      onComplete(otpString);
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').replace(/[^0-9]/g, '');
    handleAutoFill(pastedData);
  };

  return (
    <>
      {/* Hidden input for Web OTP API */}
      <HiddenInput
        ref={hiddenInputRef}
        type="text"
        autoComplete="one-time-code"
        inputMode="numeric"
        tabIndex={-1}
      />

      <OTPContainer>
        {otp.map((digit, index) => (
          <OTPBox
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={disabled}
            hasError={hasError}
            autoComplete="off"
          />
        ))}
      </OTPContainer>
    </>
  );
};

export default OTPInput;
